import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Copy, Share2, Sparkles } from "lucide-react";

/**
 * Build a warm, ready-to-paste social caption for a vendor.
 * Prefers the vendor's upcoming events; falls back to their categories & bio.
 */
function buildCaption({ vendor, events, url }) {
  const name = vendor.business_name || vendor.name || "This maker";
  const handle = vendor.slug ? `@${vendor.slug}` : "";
  const parts = [];

  if (events && events.length > 0) {
    const e = events[0];
    // Warm hero line — the future is more shareable than the past.
    parts.push(`Catch ${name}${handle ? ` (${handle})` : ""} at ${e.title}${e.date ? ` — ${e.date}` : ""}${e.city ? ` in ${e.city}` : ""}.`);
    if (events.length > 1) {
      parts.push(`Plus ${events.length - 1} more upcoming event${events.length - 1 === 1 ? "" : "s"} on AddVendTour.`);
    }
  } else {
    parts.push(`Discover ${name}${handle ? ` (${handle})` : ""} on AddVendTour.`);
  }

  if ((vendor.categories || []).length > 0) {
    parts.push(vendor.categories.slice(0, 3).map((c) => `#${String(c).replace(/\s+/g, "")}`).join(" ") + " #AddVendTour #LocalMakers");
  } else {
    parts.push("#AddVendTour #LocalMakers #ShopSmall");
  }

  parts.push(url);
  return parts.filter(Boolean).join("\n\n");
}

export default function SharePreviewDialog({ vendor, open, onOpenChange }) {
  const [caption, setCaption] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const url = vendor?.slug
    ? `${window.location.origin}/@${vendor.slug}`
    : window.location.href;

  useEffect(() => {
    if (!open || !vendor?.slug) return;
    setLoading(true);
    api.get(`/vendors/by-slug/${vendor.slug}/upcoming-events`)
      .then(({ data }) => {
        const evs = data?.items || [];
        setEvents(evs);
        setCaption(buildCaption({ vendor, events: evs, url }));
      })
      .catch(() => {
        setEvents([]);
        setCaption(buildCaption({ vendor, events: [], url }));
      })
      .finally(() => setLoading(false));
  }, [open, vendor]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Caption copied — paste it into Instagram, TikTok, anywhere.");
    } catch {
      toast.error("Could not copy — try selecting the text manually.");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      copy();
      return;
    }
    try {
      await navigator.share({
        title: vendor.business_name || vendor.name || "AddVendTour",
        text: caption,
        url,
      });
    } catch {
      // User cancelled — no-op
    }
  };

  const regenerate = () => setCaption(buildCaption({ vendor, events, url }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">Share your page</DialogTitle>
          <DialogDescription>
            One-tap caption for Instagram, TikTok, or anywhere else. Edit as much as you want before posting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {loading ? (
            <div className="py-8 text-center text-sm" style={{ color: "var(--ink-soft)" }}>
              Building your caption…
            </div>
          ) : (
            <>
              <Textarea
                data-testid="share-caption-textarea"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={9}
                className="font-mono text-sm leading-relaxed"
              />
              <div className="flex items-center justify-between text-xs" style={{ color: "var(--ink-soft)" }}>
                <span data-testid="share-char-count">{caption.length} characters</span>
                <button
                  type="button"
                  data-testid="share-regenerate"
                  onClick={regenerate}
                  className="inline-flex items-center gap-1 underline hover:no-underline"
                >
                  <Sparkles className="h-3 w-3" /> Regenerate
                </button>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="share-close"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          {typeof navigator !== "undefined" && "share" in navigator && (
            <Button
              type="button"
              data-testid="share-native"
              className="rounded-full"
              variant="outline"
              onClick={nativeShare}
            >
              <Share2 className="h-4 w-4 mr-1.5" /> Share
            </Button>
          )}
          <Button
            type="button"
            data-testid="share-copy"
            className="btn-primary rounded-full"
            onClick={copy}
            disabled={!caption}
          >
            <Copy className="h-4 w-4 mr-1.5" /> Copy caption
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
